import React, {
  FormEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import { ArrowLeft, MoreHorizontal, Search } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteMemoryAction,
  fetchAndUpdateMemoryAction,
  fetchMemoryAudioUrlAction,
  updateMemoryTitleAction,
} from "../../state/memories/actions";
import { State } from "../../state/state";
import { formatRecordingDuration } from "../../state/memories/utils";
import { getMemoriesPath } from "../../Router/routes";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import {
  AudioReplayPlayer,
  AudioReplayPlayerHandle,
} from "./AudioReplayPlayer";
import styles from "./styles.stylesheet.module.scss";

type DetailTab = "summary" | "transcript";
type TranscriptSegment =
  NonNullable<State["memories"]["items"][string]["transcript"]>["segments"][number];
type TranscriptPlaybackState = "active" | "past" | "upcoming";

const detailTabs: { id: DetailTab; label: string }[] = [
  { id: "summary", label: "Summary" },
  { id: "transcript", label: "Transcript" },
];

const formatDetailDay = (dateValue: string) => {
  const date = new Date(dateValue);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const sameDay = (a: Date, b: Date) =>
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear();

  if (sameDay(date, today)) return "Today";
  if (sameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDetailTime = (dateValue: string) => {
  return new Date(dateValue).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatTranscriptTimestamp = (timestamp: string) => {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
};

const transcriptSeekSeconds = (startedAt: string, timestamp: string) => {
  const seconds =
    (new Date(timestamp).getTime() - new Date(startedAt).getTime()) / 1000;
  return Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
};

const getTranscriptSegmentKey = (segment: TranscriptSegment) => {
  return `${segment.start_at}-${segment.end_at}-${segment.speaker}`;
};

const getTranscriptPlaybackState = (
  segment: TranscriptSegment,
  startedAt: string,
  playbackPosition: number,
): TranscriptPlaybackState => {
  const startSeconds = transcriptSeekSeconds(startedAt, segment.start_at);
  const endSeconds = Math.max(
    transcriptSeekSeconds(startedAt, segment.end_at),
    startSeconds + 0.25,
  );

  if (playbackPosition >= startSeconds && playbackPosition < endSeconds) {
    return "active";
  }
  if (playbackPosition >= endSeconds) {
    return "past";
  }
  return "upcoming";
};

const filterTranscriptSegments = (
  segments: NonNullable<State["memories"]["items"][string]["transcript"]>["segments"],
  query: string,
) => {
  const trimmedQuery = query.trim().toLowerCase();
  if (!trimmedQuery) return segments;

  return segments.filter((segment) =>
    segment.text.toLowerCase().includes(trimmedQuery),
  );
};

const countTextMatches = (text: string, query: string) => {
  const trimmedQuery = query.trim().toLowerCase();
  if (!trimmedQuery) return 0;

  const lowerText = text.toLowerCase();
  let count = 0;
  let cursor = 0;
  while (cursor < lowerText.length) {
    const matchIndex = lowerText.indexOf(trimmedQuery, cursor);
    if (matchIndex === -1) break;
    count += 1;
    cursor = matchIndex + trimmedQuery.length;
  }
  return count;
};

const getHighlightedTextParts = (text: string, query: string) => {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return [{ text, isMatch: false }];

  const lowerText = text.toLowerCase();
  const lowerQuery = trimmedQuery.toLowerCase();
  const segments: { text: string; isMatch: boolean }[] = [];
  let cursor = 0;

  while (cursor < text.length) {
    const matchIndex = lowerText.indexOf(lowerQuery, cursor);
    if (matchIndex === -1) {
      segments.push({ text: text.slice(cursor), isMatch: false });
      break;
    }

    if (matchIndex > cursor) {
      segments.push({ text: text.slice(cursor, matchIndex), isMatch: false });
    }

    segments.push({
      text: text.slice(matchIndex, matchIndex + trimmedQuery.length),
      isMatch: true,
    });
    cursor = matchIndex + trimmedQuery.length;
  }

  return segments.length ? segments : [{ text, isMatch: false }];
};

const MemoryDetailSkeleton = () => (
  <main
    className={styles.memoryDetailPage}
    aria-busy="true"
    aria-label="Loading memory"
  >
    <div className={styles.skeletonBackLink}>
      <Skeleton className={styles.detailSkeleton} height={18} width={132} />
    </div>

    <header className={styles.memoryDetailHeader}>
      <div className={styles.skeletonHeaderText}>
        <Skeleton
          className={styles.detailSkeleton}
          height={58}
          width="min(520px, 72vw)"
        />
        <div className={styles.skeletonDetailMeta}>
          <Skeleton className={styles.detailSkeleton} height={16} width={52} />
          <Skeleton className={styles.detailSkeleton} height={16} width={64} />
          <Skeleton className={styles.detailSkeleton} height={16} width={44} />
        </div>
      </div>
      <div className={styles.skeletonActions}>
        <Skeleton
          className={styles.detailSkeleton}
          height={42}
          radius="50%"
          width={42}
        />
      </div>
    </header>

    <section className={styles.detailTabbedPanel}>
      <section className={styles.skeletonPlaybackPanel}>
        <div className={styles.skeletonTimeRow}>
          <Skeleton className={styles.detailSkeleton} height={11} width={62} />
          <Skeleton className={styles.detailSkeleton} height={11} width={76} />
          <Skeleton className={styles.detailSkeleton} height={11} width={62} />
        </div>
        <div className={styles.skeletonSeekRow}>
          <Skeleton
            className={styles.detailSkeleton}
            height={4}
            radius={999}
            width="100%"
          />
        </div>
        <div className={styles.skeletonControlsRow}>
          <Skeleton
            className={styles.detailSkeleton}
            height={32}
            radius="50%"
            width={32}
          />
          <Skeleton
            className={styles.detailSkeleton}
            height={40}
            radius="50%"
            width={40}
          />
          <Skeleton
            className={styles.detailSkeleton}
            height={32}
            radius="50%"
            width={32}
          />
        </div>
      </section>

      <div className={classNames(styles.detailTabs, styles.skeletonDetailTabs)}>
        <Skeleton className={styles.detailSkeleton} height={18} width={78} />
        <Skeleton className={styles.detailSkeleton} height={18} width={86} />
      </div>
      <div className={styles.detailTabContent}>
        <article className={styles.summaryPanel}>
          <section className={styles.summarySection}>
            <Skeleton
              className={styles.detailSkeleton}
              height={17}
              width={112}
            />
            <div className={styles.skeletonTextBlock}>
              <Skeleton className={styles.detailSkeleton} height={17} />
              <Skeleton className={styles.detailSkeleton} height={17} />
              <Skeleton
                className={styles.detailSkeleton}
                height={17}
                width="72%"
              />
            </div>
          </section>
          <section className={styles.summarySection}>
            <Skeleton
              className={styles.detailSkeleton}
              height={17}
              width={104}
            />
            <div className={styles.skeletonTextBlock}>
              <Skeleton
                className={styles.detailSkeleton}
                height={17}
                width="92%"
              />
              <Skeleton
                className={styles.detailSkeleton}
                height={17}
                width="86%"
              />
              <Skeleton
                className={styles.detailSkeleton}
                height={17}
                width="94%"
              />
              <Skeleton
                className={styles.detailSkeleton}
                height={17}
                width="78%"
              />
            </div>
          </section>
        </article>
      </div>
    </section>
  </main>
);

export const MemoryDetail = () => {
  const { memoryId } = useParams<{ memoryId: string }>();
  const dispatch = useDispatch<ThunkDispatch<State, unknown, AnyAction>>();
  const navigate = useNavigate();

  const memory = useSelector((state: State) =>
    memoryId ? state.memories.items[memoryId] : undefined,
  );

  const [isLoading, setIsLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailTab>("summary");
  const [titleInput, setTitleInput] = useState("");
  const [transcriptSearch, setTranscriptSearch] = useState("");
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [isUpdatingTitle, setIsUpdatingTitle] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);
  const audioPlayerRef = useRef<AudioReplayPlayerHandle>(null);
  const transcriptSegmentRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const isMutating = isUpdatingTitle || isDeleting;

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [memoryId]);

  const initDetail = useCallback(async () => {
    if (!memoryId || isDeleting) return;

    const shouldFetchMemory =
      !memory ||
      (!memory.is_processing &&
        (memory.transcript === null || memory.transcript === undefined));
    const shouldFetchAudioUrl =
      Boolean(memory) &&
      !memory?.is_processing &&
      !memory?.audioUrl?.startsWith("blob:");
    if (!shouldFetchMemory && !shouldFetchAudioUrl) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    if (shouldFetchMemory) {
      await dispatch(
        fetchAndUpdateMemoryAction(memoryId, { requireTranscript: true }),
      );
    }
    if (shouldFetchAudioUrl) {
      await dispatch(fetchMemoryAudioUrlAction(memoryId));
    }
    setIsLoading(false);
  }, [memoryId, memory, isDeleting, dispatch]);

  useEffect(() => {
    initDetail();
  }, [initDetail]);

  useEffect(() => {
    if (memory) {
      setTitleInput(memory.name);
    }
  }, [memory]);

  useEffect(() => {
    if (!isActionsOpen) return;

    const handleClick = (event: MouseEvent) => {
      if (!actionsRef.current?.contains(event.target as Node)) {
        setIsActionsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isActionsOpen]);

  const handleTitleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!memoryId || !titleInput.trim()) return;

    setIsUpdatingTitle(true);
    await dispatch(updateMemoryTitleAction(memoryId, titleInput.trim()));
    setIsEditOpen(false);
    setIsUpdatingTitle(false);
  };

  const handleDelete = async () => {
    if (!memoryId) return;
    setIsDeleting(true);
    await dispatch(deleteMemoryAction(memoryId));
    navigate(getMemoriesPath(), { replace: true });
  };

  const memoryTranscriptSegments = memory?.transcript?.segments ?? [];
  const visibleTranscriptSegments = useMemo(
    () => filterTranscriptSegments(memoryTranscriptSegments, transcriptSearch),
    [memoryTranscriptSegments, transcriptSearch],
  );
  const transcriptMatchCount = useMemo(
    () =>
      memoryTranscriptSegments.reduce(
        (count, segment) =>
          count + countTextMatches(segment.text, transcriptSearch),
        0,
      ),
    [memoryTranscriptSegments, transcriptSearch],
  );
  const activeTranscriptSegmentKey = useMemo(() => {
    if (!memory?.started_at) return null;

    const activeSegment = memoryTranscriptSegments.find(
      (segment) =>
        getTranscriptPlaybackState(
          segment,
          memory.started_at,
          playbackPosition,
        ) === "active",
    );

    return activeSegment ? getTranscriptSegmentKey(activeSegment) : null;
  }, [memory?.started_at, memoryTranscriptSegments, playbackPosition]);
  const activeTabIndex = Math.max(
    detailTabs.findIndex((tab) => tab.id === activeTab),
    0,
  );

  useEffect(() => {
    if (activeTab !== "transcript" || !activeTranscriptSegmentKey) return;

    const activeElement = transcriptSegmentRefs.current[activeTranscriptSegmentKey];
    if (!activeElement) return;

    activeElement.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [activeTab, activeTranscriptSegmentKey, visibleTranscriptSegments]);

  if (!memoryId) {
    return <div className={styles.appCenterState}>Memory not found.</div>;
  }

  if (isLoading) {
    return <MemoryDetailSkeleton />;
  }

  if (!memory) {
    return (
      <main className={styles.memoryDetailPage}>
        <div className={styles.appCenterState}>
          <h2>Memory not found</h2>
          <Link to={getMemoriesPath()}>Back to memories</Link>
        </div>
      </main>
    );
  }

  if (memory.is_processing || !memory.audioUrl) {
    return <MemoryDetailSkeleton />;
  }

  const summary = memory.summary ?? {
    quick_summary: "",
    key_takeaways: [],
  };

  return (
    <main className={styles.memoryDetailPage}>
      <Link className={styles.backLink} to={getMemoriesPath()}>
        <ArrowLeft aria-hidden="true" size={18} strokeWidth={1.9} />
        <span>Back to memories</span>
      </Link>
      <header className={styles.memoryDetailHeader}>
        <div>
          <h1>{memory.name}</h1>
          <div className={styles.detailMeta}>
            <span>{formatDetailDay(memory.started_at)}</span>
            <span>{formatDetailTime(memory.started_at)}</span>
            <span>{formatRecordingDuration(memory.duration_seconds)}</span>
          </div>
        </div>
        <div className={styles.detailActions} ref={actionsRef}>
          <button
            className={styles.actionMenuTrigger}
            type="button"
            aria-label="Open memory actions"
            aria-expanded={isActionsOpen}
            onClick={() => setIsActionsOpen((open) => !open)}
            disabled={isMutating}
          >
            <MoreHorizontal aria-hidden="true" size={20} strokeWidth={2} />
          </button>
          {isActionsOpen && (
            <div className={styles.detailActionsMenu}>
              <button
                className={styles.detailActionsMenuItem}
                type="button"
                onClick={() => {
                  setIsActionsOpen(false);
                  setIsEditOpen(true);
                }}
                disabled={isMutating}
              >
                Edit title
              </button>
              <button
                className={classNames(
                  styles.detailActionsMenuItem,
                  styles.detailActionsMenuDanger,
                )}
                type="button"
                onClick={() => {
                  setIsActionsOpen(false);
                  setIsDeleteOpen(true);
                }}
                disabled={isMutating}
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </header>

      <section className={styles.detailTabbedPanel}>
        <AudioReplayPlayer
          ref={audioPlayerRef}
          durationSeconds={memory.duration_seconds}
          onPositionChange={setPlaybackPosition}
          src={memory.audioUrl}
          startedAt={memory.started_at}
        />

        <div
          className={styles.detailTabs}
          role="tablist"
          aria-label="Details"
          style={
            {
              "--tab-indicator-offset": `${activeTabIndex * 100}%`,
            } as React.CSSProperties
          }
        >
          <span className={styles.detailTabIndicator} aria-hidden="true" />
          {detailTabs.map((tab) => (
            <button
              key={tab.id}
              className={classNames(styles.detailTab, {
                [styles.detailTabActive]: activeTab === tab.id,
              })}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className={styles.detailTabContent}>
          {activeTab === "summary" && (
            <article className={styles.summaryPanel} role="tabpanel">
              <section className={styles.summarySection}>
                <h2>Quick Summary</h2>
                <p>{summary.quick_summary || "No summary available."}</p>
              </section>
              <section className={styles.summarySection}>
                <h2>Key Takeaways</h2>
                {summary.key_takeaways?.length ? (
                  <ul>
                    {summary.key_takeaways.map((takeaway, index) => (
                      <li key={`${takeaway}-${index}`}>{takeaway}</li>
                    ))}
                  </ul>
                ) : (
                  <p>No key takeaways available.</p>
                )}
              </section>
            </article>
          )}
          {activeTab === "transcript" && (
            <article className={styles.transcriptPanel} role="tabpanel">
              <div className={styles.transcriptSearchBar}>
                <label>
                  <Search aria-hidden="true" size={18} strokeWidth={1.9} />
                  <input
                    type="search"
                    value={transcriptSearch}
                    onChange={(event) =>
                      setTranscriptSearch(event.target.value)
                    }
                    placeholder="Find keywords..."
                  />
                </label>
              </div>
              {transcriptSearch.trim() && (
                <p className={styles.transcriptMatchCount}>
                  {transcriptMatchCount === 1
                    ? "1 match"
                    : `${transcriptMatchCount} matches`}
                </p>
              )}
              {memoryTranscriptSegments.length && visibleTranscriptSegments.length ? (
                <div className={styles.transcriptTimeline}>
                  {visibleTranscriptSegments.map((segment, index) => {
                    const segmentKey = getTranscriptSegmentKey(segment);
                    const playbackState = getTranscriptPlaybackState(
                      segment,
                      memory.started_at,
                      playbackPosition,
                    );

                    return (
                      <button
                        ref={(element) => {
                          transcriptSegmentRefs.current[segmentKey] = element;
                        }}
                        className={classNames(styles.transcriptSegment, {
                          [styles.transcriptSegmentActive]:
                            playbackState === "active",
                          [styles.transcriptSegmentPast]:
                            playbackState === "past",
                        })}
                        key={`${segmentKey}-${index}`}
                        type="button"
                        onClick={() =>
                          audioPlayerRef.current?.seekTo(
                            transcriptSeekSeconds(
                              memory.started_at,
                              segment.start_at,
                            ),
                          )
                        }
                      >
                        <span className={styles.transcriptSegmentMeta}>
                          <strong>{segment.speaker}</strong>
                          <span className={styles.transcriptTimestamp}>
                            {formatTranscriptTimestamp(segment.start_at)}
                          </span>
                        </span>
                        <span className={styles.transcriptSegmentText}>
                          {getHighlightedTextParts(
                            segment.text,
                            transcriptSearch,
                          ).map((part, partIndex) =>
                            part.isMatch ? (
                              <mark key={`${part.text}-${partIndex}`}>
                                {part.text}
                              </mark>
                            ) : (
                              <span key={`${part.text}-${partIndex}`}>
                                {part.text}
                              </span>
                            ),
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className={styles.transcriptEmpty}>
                  {memoryTranscriptSegments.length
                    ? "No matching transcript segments."
                    : "No transcript available."}
                </p>
              )}
            </article>
          )}
        </div>
      </section>

      {isEditOpen && (
        <div className={styles.modalBackdrop} role="presentation">
          <form className={styles.modalCard} onSubmit={handleTitleSubmit}>
            <h2>Edit Title</h2>
            <input
              value={titleInput}
              onChange={(event) =>
                setTitleInput(event.target.value.slice(0, 80))
              }
              maxLength={80}
              disabled={isMutating}
              autoFocus
            />
            <p className={styles.charCount}>{titleInput.length}/80</p>
            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={() => setIsEditOpen(false)}
                disabled={isMutating}
              >
                Cancel
              </button>
              <button
                className={styles.primaryButton}
                type="submit"
                disabled={isMutating || !titleInput.trim()}
              >
                {isUpdatingTitle ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}

      {isDeleteOpen && (
        <div className={styles.modalBackdrop} role="presentation">
          <div className={styles.modalCard}>
            <h2>Delete Memory?</h2>
            <p>This will permanently delete the memory and all related data.</p>
            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={() => setIsDeleteOpen(false)}
                disabled={isMutating}
              >
                Cancel
              </button>
              <button
                className={classNames(styles.dangerButton, styles.filled)}
                type="button"
                onClick={handleDelete}
                disabled={isMutating}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};
